import { useEffect, useRef, useState } from "react";
import { Download, ExternalLink, X } from "lucide-react";

interface Props {
  /** 상단에 표시할 문서명 */
  name: string;
  /** iframe 으로 열 URL (PDF/이미지/HTML) */
  url: string;
  /** 원본 다운로드 URL — 있으면 '다운로드' 버튼 표시 */
  downloadUrl?: string;
  /** 하단 안내문(선택) */
  note?: string;
  onClose: () => void;
}

/**
 * 문서 인라인 뷰어(모달).
 *
 * 첨부·산출물을 다운로드하지 않고 페이지 안에서 바로 본다. Office 문서는 서버(rag-service)
 * 가 PDF 로 변환해 내려주므로 브라우저 기본 PDF 뷰어로 렌더된다.
 * 변환 실패·미지원 형식은 서버가 안내 화면을 주고, 사용자는 상단 '다운로드' 로 폴백한다.
 */
export default function DocViewer({ name, url, downloadUrl, note, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const closeRef = useRef<HTMLButtonElement>(null);

  // ESC 닫기 + 배경 스크롤 잠금 + 진입 시 닫기 버튼 포커스
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-0 sm:p-3"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`${name} 미리보기`}
    >
      <div className="flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-none bg-card shadow-xl sm:h-[92vh] sm:rounded-xl">
        <div className="flex items-center gap-1.5 border-b border-border px-3 py-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium" title={name}>
            {name}
          </span>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
            title="새 탭에서 열기"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">새 탭</span>
          </a>
          {downloadUrl && (
            <a
              href={downloadUrl}
              download
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
              title="원본 다운로드"
            >
              <Download className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">다운로드</span>
            </a>
          )}
          <button
            ref={closeRef}
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted"
            aria-label="닫기"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative min-h-0 flex-1 bg-muted/40">
          <iframe
            src={url}
            title={`${name} 미리보기`}
            className="h-full w-full bg-background"
            onLoad={() => setLoading(false)}
          />
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-muted/40 text-xs text-muted-foreground">
              <span className="h-7 w-7 animate-spin rounded-full border-[3px] border-border border-t-[#0E1E2D]" />
              문서를 준비하고 있습니다…
            </div>
          )}
        </div>

        {note && (
          <div className="border-t border-border px-3 py-1.5 text-center text-[11px] text-muted-foreground">
            {note}
          </div>
        )}
      </div>
    </div>
  );
}
